import React from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { confirmAlert } from 'react-confirm-alert';
import DatePicker from 'react-datepicker';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { formatCurrency } from '@C/TableGrid';
import { authHeader } from '@H';
import { authService } from '@S';
import {
  buildDateTimeFromTimeValue,
  getShiftTimingConfig,
  isOvernightShift,
  parseLocalDate,
  TIME_INTERVAL_MINUTES,
} from '@stores/_utils/bunkConfig';
import { useGlobalStore } from '@stores/globalStore';
import { useShallow } from 'zustand/react/shallow';

import './styles.scoped.scss';

const getInitialEndDutyDateTime = () =>
  dayjs().second(0).millisecond(0).toDate();

const TotalSummary = (props) => {
  const duty = props.duty;
  const staff = props.staff;
  const date = props.date;
  const dutyTimeStamp = props.dutyTimeStamp;
  const savedEndDutyTimeStamp = props.savedEndDutyTimeStamp;
  const assignDutyMoment =
    dutyTimeStamp && dayjs(dutyTimeStamp).isValid()
      ? dayjs(dutyTimeStamp)
      : parseLocalDate(date);
  const history = useHistory();
  const { mode } = useParams();
  const isViewMode = props.isViewMode || (mode || '').includes('view-duty');
  const isEditingSavedEndTime =
    (mode === 'edit' || isViewMode) &&
    Boolean(savedEndDutyTimeStamp) &&
    dayjs(savedEndDutyTimeStamp).isValid();

  const {
    codGrandTotal,
    codTotalCollected,
    codStaff,
    codShift,
    codShiftId,
    shortage,
    bunkConfig,
    shiftList,
    timeLoggingEnabled,
  } = useGlobalStore(
    useShallow((state) => ({
      codGrandTotal: state.codGrandTotal,
      codTotalCollected: state.codTotalCollected,
      codStaff: state.codStaff,
      codShift: state.codShift,
      codShiftId: state.codShiftId,
      shortage: state.shortage,
      bunkConfig: state.bunkConfig,
      shiftList: state.shiftList,
      timeLoggingEnabled: Boolean(state.bunkConfig?.enable_time_cod),
    })),
  );
  const [endDutyTimeStamp, setEndDutyTimeStamp] = React.useState(() =>
    isEditingSavedEndTime
      ? dayjs(savedEndDutyTimeStamp).toDate()
      : getInitialEndDutyDateTime(),
  );
  const minEndDutyDate = assignDutyMoment.isValid()
    ? assignDutyMoment.toDate()
    : null;
  const maxEndDutyDate = assignDutyMoment.isValid()
    ? assignDutyMoment.add(1, 'day').endOf('day').toDate()
    : dayjs().add(1, 'day').toDate();

  React.useEffect(() => {
    setEndDutyTimeStamp(
      isEditingSavedEndTime
        ? dayjs(savedEndDutyTimeStamp).toDate()
        : getInitialEndDutyDateTime(),
    );
  }, [date, isEditingSavedEndTime, savedEndDutyTimeStamp]);

  React.useEffect(() => {
    if (!timeLoggingEnabled || isEditingSavedEndTime) {
      return;
    }

    const shiftIdentifier = codShiftId || codShift;
    if (!shiftIdentifier) {
      return;
    }

    const { start_time, end_time } = getShiftTimingConfig(
      bunkConfig,
      shiftIdentifier,
      shiftList,
    );

    const endDutyDate = isOvernightShift(start_time, end_time)
      ? parseLocalDate(date).add(1, 'day').format('YYYY-MM-DD')
      : date;

    setEndDutyTimeStamp(
      buildDateTimeFromTimeValue(endDutyDate, end_time, endDutyTimeStamp),
    );
  }, [
    bunkConfig,
    codShift,
    codShiftId,
    date,
    isEditingSavedEndTime,
    shiftList,
    timeLoggingEnabled,
  ]);

  const confirmEndDuty = () => {
    if (isViewMode) {
      return;
    }

    const shiftStartMoment = assignDutyMoment.second(0).millisecond(0);
    const shiftEndMoment = dayjs(endDutyTimeStamp).second(0).millisecond(0);

    if (timeLoggingEnabled && shiftEndMoment.isBefore(shiftStartMoment)) {
      toast.error('End duty time cannot be before the shift start time');
      return;
    }

    const totalMinutes = shiftEndMoment.diff(shiftStartMoment, 'minute');
    const totalHours = Math.floor(totalMinutes / 60);
    const remainingMinutes = totalMinutes % 60;

    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="react-confirm-alert-body">
          <h1>{mode == 'edit' ? 'Confirm changes' : 'Confirm end of duty'}</h1>
          <p>
            <strong>Cashier:</strong> {codStaff}
          </p>
          {timeLoggingEnabled && (
            <>
              <p>
                <strong>Shift start time:</strong>{' '}
                {shiftStartMoment.format('D MMM YYYY, hh:mm A')}
              </p>
              <p>
                <strong>Shift end time:</strong>{' '}
                {shiftEndMoment.format('D MMM YYYY, hh:mm A')}
              </p>
              <p>
                <strong>Total hours:</strong> {totalHours}h {remainingMinutes}m
              </p>
            </>
          )}
          <div className="react-confirm-alert-button-group">
            <button
              onClick={() => {
                onClose();
                endDuty();
              }}
            >
              Yes
            </button>
            <button onClick={onClose}>No</button>
          </div>
        </div>
      ),
    });
  };

  const endDuty = () => {
    if (mode === 'edit') {
      history.push('/cashiers-on-duty');
      return;
    }
    const formData = {
      uuid: duty,
      ...(timeLoggingEnabled
        ? {
            end_duty_time_stamp: dayjs(endDutyTimeStamp).format(
              'YYYY-MM-DDTHH:mm:ss',
            ),
          }
        : {}),
    };

    const headers = authHeader(1);
    axios
      .put(`${process.env.API_URL}/bunk/cashier-end-duty/`, formData, {
        headers,
      })
      .then((response) => {
        toast.success('Duty ended successfully');
        if (authService.currentUserRole === 'Cashier') {
          history.push(`/dashboard`);
        } else {
          history.push(`/cashiers-on-duty/assign-duty`);
        }
      });
  };

  return (
    <section className="widget full-width summary-widget">
      <header>
        <h3>Summary</h3>
      </header>
      <main>
        <div className="staff-info">
          <span>{codStaff} </span>
          <div className="duty-info">
            <span>{codShift}</span>
            {mode !== 'edit' && !isViewMode && timeLoggingEnabled && (
              <span>{assignDutyMoment.format('D MMM, YYYY, hh:mm A')}</span>
            )}
          </div>
        </div>
        <div className="time-input">
          {(mode === 'edit' || isViewMode) &&
            (timeLoggingEnabled ? (
              <>
                <span>
                  Duty start: {assignDutyMoment.format('D MMM YYYY, hh:mm A')}
                </span>
                <span>
                  Duty end:{' '}
                  {dayjs(endDutyTimeStamp).format('D MMM YYYY, hh:mm A')}
                </span>
              </>
            ) : (
              <span>Duty date: {assignDutyMoment.format('D MMM, YYYY')}</span>
            ))}
          {mode !== 'edit' && timeLoggingEnabled && !isViewMode && (
            <DatePicker
              selected={endDutyTimeStamp}
              onChange={setEndDutyTimeStamp}
              dateFormat="d MMM yyyy h:mm aa"
              showTimeSelect
              timeFormat="h:mm aa"
              timeIntervals={TIME_INTERVAL_MINUTES}
              minDate={minEndDutyDate}
              maxDate={maxEndDutyDate}
              className="custom-datetime"
            />
          )}
          {mode !== 'edit' && !isViewMode && !timeLoggingEnabled && (
            <span>Duty date: {assignDutyMoment.format('D MMM, YYYY')}</span>
          )}
          {!isViewMode && mode !== 'edit' && (
            <button
              className="red-btn"
              onClick={confirmEndDuty}
              style={{ maxHeight: '40px' }}
            >
              End duty
            </button>
          )}
        </div>

        <div className="summary-info total-summary">
          <span>Grand Total</span>
          <span>{formatCurrency(codGrandTotal)}</span>
        </div>
        <div className="summary-info collection-summary">
          <span>Total Collected</span>
          <span>{formatCurrency(codTotalCollected)}</span>
        </div>
        <div className="summary-info shortage-sumamry">
          <span>Shortage/Excess</span>
          {(() => {
            const s = parseFloat(shortage);
            if (Math.abs(s) < 0.0001) {
              return <span className="--green"> No shortage/excess </span>;
            } else if (s > 0) {
              return <span className="--green"> {formatCurrency(s)}</span>;
            } else {
              return <span className="--red"> {formatCurrency(s)}</span>;
            }
          })()}
        </div>
      </main>
    </section>
  );
};

export { TotalSummary };
